import http from '../client';
import { errorHandler } from '../errorHandler';
import type { ApiResponse } from '../types';

export type ReviewCommentStatus = 'pending' | 'approved' | 'rejected';

export type AdminReviewComment = {
  id: number;
  review: string;
  media_url?: string | null;
  media_type?: 'image' | 'video' | string | null;
  attachments?: Array<{ url: string; type?: string | null }>;
  status: ReviewCommentStatus;
  is_doctor: boolean;
  rejection_reason?: string | null;
  created_at: string;
  reviewed_at?: string | null;
  updated_at?: string;
  user_id: number;
  object_id: number;
  user?: { id: number; name: string; email?: string } | null;
  recipe?: { id: number; uuid?: string; title?: string } | null;
};

export const reviewCommentsAdminApi = {
  list: async (params?: {
    page?: number;
    limit?: number;
    status?: ReviewCommentStatus | 'all';
  }): Promise<
    ApiResponse<{
      data: AdminReviewComment[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      pending_count: number;
    }>
  > => {
    try {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.status) query.set('status', params.status);
      const qs = query.toString();
      const response = await http.get(
        `/admin/review-comments${qs ? `?${qs}` : ''}`,
      );
      return { success: true, data: response.data?.data ?? response.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  approve: async (
    id: number,
    options?: { override?: boolean },
  ): Promise<ApiResponse<unknown>> => {
    try {
      const response = await http.post(`/admin/review-comments/${id}/approve`, {
        override: options?.override ?? true,
      });
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  reject: async (
    id: number,
    reason?: string,
    options?: { override?: boolean },
  ): Promise<ApiResponse<unknown>> => {
    try {
      const response = await http.post(`/admin/review-comments/${id}/reject`, {
        reason: reason || undefined,
        override: options?.override ?? true,
      });
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  revert: async (id: number): Promise<ApiResponse<unknown>> => {
    try {
      const response = await http.post(`/admin/review-comments/${id}/revert`);
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
};
